// src/app/invoice-details.jsx
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import API_BASE_URL from '../apiConfig.js';

// token helper
const getToken = () => {
  try { return localStorage.getItem('userToken'); } catch { return null; }
};

// Helper to convert UTC date/time to local date/time for editing
const utcToLocal = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return { localDate: '', localTime: '' };
  
  // Create UTC date from the stored values
  const utcDate = new Date(`${dateStr}T${timeStr}Z`);
  
  // Convert to local date and time strings for the inputs
  const localDate = utcDate.getFullYear() + '-' + 
    String(utcDate.getMonth() + 1).padStart(2, '0') + '-' + 
    String(utcDate.getDate()).padStart(2, '0');
  
  const localTime = String(utcDate.getHours()).padStart(2, '0') + ':' + 
    String(utcDate.getMinutes()).padStart(2, '0');
  
  return { localDate, localTime };
};

// Helper to convert local date/time back to UTC for saving
const localToUtc = (localDate, localTime) => {
  if (!localDate || !localTime) return { utcDate: '', utcTime: '' };
  
  // Create local date from the input values
  const localDateTime = new Date(`${localDate}T${localTime}`);
  
  // Convert to UTC date and time strings for the API
  const utcDate = localDateTime.getUTCFullYear() + '-' + 
    String(localDateTime.getUTCMonth() + 1).padStart(2, '0') + '-' + 
    String(localDateTime.getUTCDate()).padStart(2, '0');
  
  const utcTime = String(localDateTime.getUTCHours()).padStart(2, '0') + ':' + 
    String(localDateTime.getUTCMinutes()).padStart(2, '0') + ':00';
  
  return { utcDate, utcTime };
};

export default function InvoiceDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [invoice, setInvoice] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null); // Add business info state
  const [businessLogoUrl, setBusinessLogoUrl] = useState(null); // Add business logo URL state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [customers, setCustomers] = useState([]);

  // Custom Modal Component for better-looking dialogs
  const [customModal, setCustomModal] = useState({
    open: false,
    title: '',
    message: '',
    buttons: []
  });

  const showCustomModal = (title, message, buttons) => {
    setCustomModal({
      open: true,
      title,
      message,
      buttons
    });
  };

  const closeCustomModal = () => {
    setCustomModal({
      open: false,
      title: '',
      message: '',
      buttons: []
    });
  };

  // Check if user has quote write permissions
  const hasInvoiceWritePermissions = () => {
    return userPermissions?.invoice_perm === 'write';
  };

  // Load user permissions on component mount and check access
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const permissions = localStorage.getItem('userPermissions');
        if (permissions) {
          const parsedPermissions = JSON.parse(permissions);
          setUserPermissions(parsedPermissions);
          
          const user_type = parsedPermissions?.user_type;
          const invoicePerm = parsedPermissions?.invoice_perm;

          // Block access if user is NOT (Owner OR Admin) AND has no invoice permission
          if ((user_type !== 'owner' && user_type !== 'admin') && invoicePerm === 'none') {
            alert("You don't have permission to view invoices. Please contact your account administrator.");
            navigate('/'); // Redirect to home page
            return;
          }
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
      }
    };
    loadUserPermissions();
  }, [navigate]);

  // Load customers for preview
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const token = getToken();
        const response = await axios.get(`${API_BASE_URL}/api/customers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        const list = Array.isArray(response.data) ? response.data : response.data?.customers ?? [];
        setCustomers(list);
      } catch (error) {
        console.error('Error loading customers:', error);
      }
    };
    loadCustomers();
  }, []);

  // Check if user can see prices
  const canSeePrices = () => {
    return userPermissions?.price_perm === 'visible';
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const token = getToken();
        const { data } = await axios.get(`${API_BASE_URL}/api/invoices/id/${encodeURIComponent(id)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });

        // Store business info from backend response if available
        if (data.businessInfo) {
          setBusinessInfo(data.businessInfo);
          // Set logo preview if exists
          if (data.businessInfo?.businessLogo) {
            setBusinessLogoUrl(`${API_BASE_URL}/api/uploads/${data.businessInfo.businessLogo}`);
          }
        }

        // keep only the fields the UI edits/displays
        const filtered = {
          id: data.id,
          customer: data.customer ?? '',
          customerId: data.customerId, // Add customerId for Customer Details link
          title: data.title ?? '',
          description: data.description ?? '',
          price: data.price ?? '',
          selectedDate: data.selectedDate ?? '', // "YYYY-MM-DD"
          selectedTime: data.selectedTime ?? '', // "HH:mm:ss"
          notes: data.notes ?? '',
          sentEmail: data.sentEmail,
          sentText: data.sentText,
          dateSent: data.dateSent, // Add dateSent field
          paid: data.paid ?? 'no', // Add paid field
        };
        // ensure price is a string for TextInput
        filtered.price = String(filtered.price ?? '');

        setInvoice(filtered);
        setEditedInvoice(filtered);
      } catch (err) {
        console.error('Error loading invoice:', err);
        alert('Failed to load invoice.');
      }
    };
    load();
  }, [id]);

  const saveEdits = async () => {
    if (!invoice || !editedInvoice) return;

    // Validate required field: title
    if (!editedInvoice.title.trim()) {
      alert('Title is required. Please enter a title for the invoice.');
      return;
    }

    // Handle price: if empty or invalid, default to "0.00"
    let cleanedPrice = editedInvoice.price.trim();
    if (cleanedPrice === '' || isNaN(parseFloat(cleanedPrice))) {
      cleanedPrice = '0.00';
    }

    // Convert local date/time back to UTC for saving
    const { utcDate, utcTime } = localToUtc(editedInvoice.selectedDate, editedInvoice.selectedTime?.slice(0, 5));
    
    // Create payload with UTC date/time and cleaned data
    const payloadWithUtc = {
      ...editedInvoice,
      title: editedInvoice.title.trim(),
      description: editedInvoice.description.trim() || '', // Save as empty string if blank
      price: cleanedPrice,
      notes: editedInvoice.notes.trim() || '', // Save as empty string if blank
      selectedDate: utcDate || editedInvoice.selectedDate,
      selectedTime: utcTime || editedInvoice.selectedTime
    };

    // shallow change check
    const changed = Object.keys(payloadWithUtc).some((k) => payloadWithUtc[k] !== invoice[k]);
    if (!changed) {
      setIsEditing(false);
      return;
    }

    try {
      setSaving(true);
      const token = getToken();
      await axios.put(`${API_BASE_URL}/api/invoices/${encodeURIComponent(id)}`, payloadWithUtc, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setInvoice(payloadWithUtc);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving invoice edits:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteInvoice = async () => {
    if (!id) return;
    const ok = window.confirm('Are you sure you want to delete this invoice?');
    if (!ok) return;

    try {
      const token = getToken();
      await axios.delete(`${API_BASE_URL}/api/invoices/delete/${encodeURIComponent(id)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      navigate(-1);
    } catch (err) {
      console.error('Error deleting invoice:', err);
      alert('Failed to delete invoice.');
    }
  };

  const emailInvoicePDF = async () => {
    if (!id || sendingEmail) return;

    try {
      setSendingEmail(true);
      const token = getToken();

      // Get user's timezone offset in minutes
      const timezoneOffset = new Date().getTimezoneOffset();
          
      const response = await axios.post(`${API_BASE_URL}/api/invoices/${id}/email`, {
        timezoneOffset: timezoneOffset  // Send timezone offset to backend
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (response.data && response.data.success) {
        // Reload invoice to update sentEmail status
        const { data } = await axios.get(`${API_BASE_URL}/api/invoices/id/${encodeURIComponent(id)}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        setInvoice(prevInvoice => ({
          ...prevInvoice,
          sentEmail: 'yes',
        }));
        setEditedInvoice(prevInvoice => ({
          ...prevInvoice,
          sentEmail: 'yes',
        }));
        
        showCustomModal(
          'Email Sent Successfully! ✅',
          `Invoice has been emailed as PDF to the customer.`,
          [{ text: 'Great!', style: 'primary', onPress: closeCustomModal }]
        );
      } else {
        showCustomModal(
          'Email Status Unclear ⚠️',
          response.data?.message || 'Email may have been sent, but status is unclear.',
          [{ text: 'OK', style: 'secondary', onPress: closeCustomModal }]
        );
      }
    } catch (emailError) {
      console.error('Error sending invoice email:', emailError);
      
      let errorMessage = 'Failed to send email. Please try again.';
      
      if (emailError.response?.status === 400) {
        errorMessage = `Customer email issue. Please update customer information.`;
      } else if (emailError.response?.status === 404) {
        errorMessage = 'Invoice not found.';
      } else if (emailError.response?.data?.detail) {
        errorMessage = emailError.response.data.detail;
      } else if (emailError.response?.data?.message) {
        errorMessage = emailError.response.data.message;
      } else if (emailError.message) {
        errorMessage = `Network error: ${emailError.message}`;
      }
      
      showCustomModal(
        'Email Failed ❌',
        errorMessage,
        [{ text: 'OK', style: 'danger', onPress: closeCustomModal }]
      );
    } finally {
      setSendingEmail(false);
    }
  };

  // Function to generate PDF and trigger browser download
  const generateAndSharePDF = async () => {
    try {
      const customer = customers.find(c => c.name === invoice.customer);
      
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            .header { display: flex; justify-content: space-between; margin-bottom: 30px; border-bottom: 2px solid #dc2626; padding-bottom: 20px; }
            .logo-container { margin-bottom: 10px; min-height: 85px; }
            .business-logo { max-width: 150px; max-height: 80px; object-fit: contain; }
            .company-name { font-size: 24px; font-weight: bold; color: #dc2626; margin-bottom: 10px; }
            .company-details { font-size: 13px; color: #666; line-height: 1.6; }
            .invoice-title { font-size: 28px; font-weight: bold; color: #dc2626; }
            .invoice-details { font-size: 13px; color: #666; margin-top: 8px; }
            .section-title { font-size: 18px; font-weight: bold; margin: 30px 0 15px 0; padding-bottom: 5px; border-bottom: 2px solid #e5e7eb; }
            .customer-info { background: #fef2f2; padding: 20px; border-radius: 8px; border-left: 4px solid #dc2626; }
            .service-item { border: 1px solid #e5e7eb; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .pricing-section { background: #fef2f2; padding: 20px; border: 2px solid #dc2626; border-radius: 8px; margin: 30px 0; }
            .price-row { display: flex; justify-content: space-between; padding: 15px 0; border-bottom: 1px solid #fecaca; }
            .total-row { border-top: 2px solid #dc2626; border-bottom: none; font-size: 20px; font-weight: bold; margin-top: 10px; }
            .payment-section { background: #f0fdf4; padding: 20px; border-radius: 8px; border-left: 4px solid #22c55e; margin: 30px 0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; font-size: 13px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <div class="logo-container">
                ${businessLogoUrl ? `<img src="${businessLogoUrl}" alt="Business Logo" class="business-logo" />` : ''}
              </div>
              <div class="company-name">${businessInfo?.businessName || 'Your Business Name'}</div>
              <div class="company-details">
                ${businessInfo?.firstName || businessInfo?.lastName ? `${[businessInfo.firstName, businessInfo.lastName].filter(Boolean).join(' ')}<br>` : ''}
                ${businessInfo?.address || ''}<br>
                ${businessInfo?.city && businessInfo?.state ? `${businessInfo.city}, ${businessInfo.state} ${businessInfo.zip || ''}` : 'City, State'}<br>
                Phone: ${businessInfo?.phone || ''}<br>
                Email: ${businessInfo?.email || ''}
              </div>
            </div>
            <div style="text-align: right;">
              <div class="invoice-title">INVOICE</div>
              <div class="invoice-details">
                Invoice #: INV-${id || 'XXXX'}<br>
                Date: ${new Date().toLocaleDateString()}<br>
                Due Date: ${new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
              </div>
            </div>
          </div>
          
          <div class="section-title">Invoice To:</div>
          <div class="customer-info">
            <strong>${invoice.customer || 'Customer Name'}</strong><br>
            ${customer?.address || ''}<br>
            ${customer?.city && customer?.state ? `${customer.city}, ${customer.state} ${customer.zip || ''}` : ''}<br>
            ${customer?.phone ? `Phone: ${customer.phone}` : ''}<br>
            ${customer?.email ? `Email: ${customer.email}` : ''}
          </div>
          
          <div class="section-title">Services Provided</div>
          <div class="service-item">
            <strong style="font-size: 18px;">${invoice.title || 'Service Title'}</strong><br><br>
            ${invoice.description || 'No description provided'}<br><br>
            <em style="color: #666;">Work completed on: ${(() => {
              const utcDate = new Date(invoice.selectedDate + 'T' + invoice.selectedTime);
              const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
              const formattedDate = localDate.toLocaleDateString();
              const formattedTime = localDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
              return `${formattedDate} at ${formattedTime}`;
            })()}</em>
          </div>
          
          <div class="pricing-section">
            <div class="price-row">
              <span>Service Total:</span>
              <span><strong>$${parseFloat(invoice.price || 0).toFixed(2)}</strong></span>
            </div>
            <div class="price-row total-row">
              <span>Amount Due:</span>
              <span>$${parseFloat(invoice.price || 0).toFixed(2)}</span>
            </div>
          </div>
          
          <div class="payment-section">
            <strong>Payment Information</strong><br><br>
            • Payment is due within 30 days of invoice date<br>
            • Please include invoice number with payment<br>
            • Late payments may be subject to additional fees<br>
            • Contact us with any questions about this invoice
          </div>
          
          <div class="footer">
            <p>Thank you for your business!</p>
            <p><strong>Questions?</strong> Contact us at ${businessInfo?.phone || ''} or ${businessInfo?.email || ''}</p>
          </div>
        </body>
        </html>
      `;
      
      // Generate PDF filename
      const customerFileName = (invoice.customer || 'Customer')
        .replace(/[^a-z0-9\s]/gi, '')  // Remove special characters
        .trim()
        .replace(/\s+/g, '_');  // Replace spaces with underscores
      
      const pdfFileName = `${customerFileName}_Invoice.pdf`;
      
      // Create a temporary iframe to render the HTML and trigger print
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);
      
      const printDocument = printFrame.contentWindow.document;
      printDocument.open();
      printDocument.write(htmlContent);
      printDocument.close();
      
      // Set the document title for the PDF filename
      printDocument.title = pdfFileName;
      
      // Wait for content to load, then trigger print
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 1000);
      }, 500);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const prettyLocal = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return '';
    // Treat stored values as UTC and render in local time
    const d = new Date(`${dateStr}T${timeStr}Z`);
    return format(d, 'MMMM dd, yyyy hh:mm a');
  };

  // Helper to format local date/time for edit mode preview (no UTC conversion needed)
  const prettyLocalEdit = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return '';
    const d = new Date(`${dateStr}T${timeStr}`);
    return format(d, 'MMMM dd, yyyy hh:mm a');
  };

  if (!id) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>No invoice id provided.</Text>
      </View>
    );
  }

  if (!invoice || !editedInvoice) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <Text>Loading…</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ minHeight: '100vh', backgroundColor: '#f0f8ff' }}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
        {/* Customer */}
        <Text style={styles.label}>Customer:</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={styles.value}>{invoice.customer}</Text>
          <TouchableOpacity onPress={() => navigate(`/customer-details?id=${invoice.customerId}`)}>
            <Text style={styles.customerDetailsLink}>Customer Details</Text>
          </TouchableOpacity>
        </View>

        {/* Title */}
        <Text style={styles.label}>Title:</Text>
        {isEditing ? (
          <TextInput
            style={styles.input}
            value={editedInvoice.title}
            onChangeText={(t) => setEditedInvoice((p) => ({ ...p, title: t }))}
          />
        ) : (
          <Text style={styles.value}>{invoice.title}</Text>
        )}

        {/* Description */}
        <Text style={styles.label}>Description:</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 100 }]}
            multiline
            value={editedInvoice.description}
            onChangeText={(t) => setEditedInvoice((p) => ({ ...p, description: t }))}
          />
        ) : (
          <Text style={styles.value}>{invoice.description || 'No Description'}</Text>
        )}

        {/* Price */}
        {canSeePrices() && (
          <>
            <Text style={styles.label}>Price:</Text>
            {isEditing ? (
              <TextInput
                style={styles.input}
                keyboardType="numeric"
                value={editedInvoice.price}
                onChangeText={(t) => setEditedInvoice((p) => ({ ...p, price: t }))}
              />
            ) : (
              <Text style={styles.value}>{`$${invoice.price}`}</Text>
            )}
          </>
        )}

        {/* Work Completed */}
        <Text style={styles.label}>Work Completed:</Text>
        {isEditing ? (
          <View style={{ gap: 8 }}>
            <View style={styles.dateTimeRow}>
              {/* HTML date/time inputs for web */}
              <input
                type="date"
                value={editedInvoice.selectedDate || ''}
                onChange={(e) =>
                  setEditedInvoice((p) => ({ ...p, selectedDate: e.target.value }))
                }
                style={htmlInputStyle}
              />
              <input
                type="time"
                value={(editedInvoice.selectedTime || '').slice(0, 5)}
                onChange={(e) => {
                  const v = e.target.value; // HH:mm
                  const withSeconds = v && v.length === 5 ? `${v}:00` : v;
                  setEditedInvoice((p) => ({ ...p, selectedTime: withSeconds }));
                }}
                style={htmlInputStyle}
              />
            </View>

            <View style={styles.datePreview}>
              <Text style={styles.datePreviewText}>
                {prettyLocalEdit(editedInvoice.selectedDate, editedInvoice.selectedTime) ||
                  'Select a valid date and time'}
              </Text>
            </View>
          </View>
        ) : (
          <Text style={styles.value}>
            {prettyLocal(invoice.selectedDate, invoice.selectedTime)}
          </Text>
        )}

        {/* Payment Status */}
        <Text style={styles.label}>Payment Status:</Text>
        {isEditing ? (
          <View style={styles.paymentContainer}>
            <TouchableOpacity
              style={styles.paymentOption}
              onPress={() => setEditedInvoice((p) => ({ ...p, paid: 'no' }))}
            >
              <View style={[styles.checkbox, editedInvoice.paid === 'no' && styles.checkboxSelected]}>
                {editedInvoice.paid === 'no' && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.paymentOptionText}>Not Paid</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.paymentOption}
              onPress={() => setEditedInvoice((p) => ({ ...p, paid: 'yes' }))}
            >
              <View style={[styles.checkbox, editedInvoice.paid === 'yes' && styles.checkboxSelected]}>
                {editedInvoice.paid === 'yes' && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.paymentOptionText}>Paid in Full</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <View style={styles.paymentStatusContainer}>
            <Text style={styles.value}>
              {invoice.paid === 'yes' ? 'Paid in Full' : 'Not Paid'}
            </Text>
          </View>
        )}

        {/* Notes */}
        <Text style={styles.label}>Notes:</Text>
        {isEditing ? (
          <TextInput
            style={[styles.input, { height: 100 }]}
            multiline
            value={editedInvoice.notes}
            onChangeText={(t) => setEditedInvoice((p) => ({ ...p, notes: t }))}
          />
        ) : (
          <Text style={styles.value}>{invoice.notes || 'No notes available'}</Text>
        )}

        {/* Sent */}
        <Text style={styles.label}>Sent:</Text>
        <View style={styles.sentContainer}>
          <View style={styles.sentStatusContainer}>
            <Text style={styles.value}>
              {invoice.sentEmail === 'yes' || invoice.sentText === 'yes' ? 'Yes' : 'No'}
            </Text>
            {/* Show dateSent if email was sent */}
            {invoice.sentEmail === 'yes' && invoice.dateSent && (
              <Text style={styles.dateSentText}>
                Emailed: {format(new Date(invoice.dateSent), 'MMM dd, yyyy hh:mm a')}
              </Text>
            )}
          </View>
          
          {/* Action buttons next to sent status */}
          <View style={styles.sentButtonsContainer}>
            {invoice.sentEmail === 'yes' || invoice.sentText === 'yes' ? (
              // If already sent, show Resend and View Invoice buttons
              <>
                <TouchableOpacity
                  style={[styles.actionButton, styles.resendButton, sendingEmail && { opacity: 0.7 }]}
                  onPress={emailInvoicePDF}
                  disabled={sendingEmail}
                >
                  <Text style={styles.actionButtonText}>
                    {sendingEmail ? 'Sending…' : 'Resend'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.actionButton, styles.previewButton]}
                  onPress={() => setPreviewOpen(true)}
                >
                  <Text style={styles.actionButtonText}>View Invoice</Text>
                </TouchableOpacity>
              </>
            ) : (
              // If not sent, show Email PDF to Customer and Preview PDF buttons
              <>
                <TouchableOpacity
                  style={[styles.actionButton, styles.emailButton, sendingEmail && { opacity: 0.7 }]}
                  onPress={emailInvoicePDF}
                  disabled={sendingEmail}
                >
                  <Text style={styles.actionButtonText}>
                    {sendingEmail ? 'Sending…' : 'Email PDF to Customer'}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.actionButton, styles.previewButton]}
                  onPress={() => setPreviewOpen(true)}
                >
                  <Text style={styles.actionButtonText}>Preview PDF</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>

        {/* Buttons */}
        {isEditing ? (
          <View style={styles.buttonContainer}>
            <View style={styles.rowContainer}>
              <TouchableOpacity
                style={[styles.button, styles.saveButton, saving && { opacity: 0.7 }]}
                onPress={saveEdits}
                disabled={saving}
              >
                <Text style={styles.buttonText}>{saving ? 'Saving…' : 'Save'}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={() => {
                  setEditedInvoice({ ...invoice });
                  setIsEditing(false);
                }}
              >
                <Text style={styles.buttonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          hasInvoiceWritePermissions() && (
          <View style={styles.rowContainer}>
            <TouchableOpacity 
              style={[styles.button, styles.editButton]} 
              onPress={() => {
                // Convert UTC date/time to local time for editing
                const { localDate, localTime } = utcToLocal(invoice.selectedDate, invoice.selectedTime);
                const editInvoiceWithLocalTime = {
                  ...invoice,
                  selectedDate: localDate || invoice.selectedDate,
                  selectedTime: localTime ? `${localTime}:00` : invoice.selectedTime
                };
                setEditedInvoice(editInvoiceWithLocalTime);
                setIsEditing(true);
              }}
            >
              <Text style={styles.buttonText}>Edit</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.button, styles.deleteButton]} onPress={deleteInvoice}>
              <Text style={styles.buttonText}>Delete</Text>
            </TouchableOpacity>
          </View>
          )
        )}
      </ScrollView>

      {/* Preview Modal - matching mobile invoice-details.js exactly */}
      {previewOpen && (
        <View style={styles.previewOverlay}>
          <View style={styles.previewCard}>
            <ScrollView style={{ flex: 1, padding: 20 }}>
              {/* Header Section */}
              <View style={styles.previewHeader}>
                <View style={styles.previewCompanyInfo}>
                  {businessLogoUrl ? (
                    <img 
                      src={businessLogoUrl}
                      alt="Business Logo"
                      style={{
                        maxWidth: '150px',
                        maxHeight: '100px',
                        objectFit: 'contain',
                        marginBottom: '15px'
                      }}
                    />
                  ) : (
                    <Text style={styles.previewLogoPlaceholder}>
                      Your business logo will appear here once uploaded by Owner/Admin in Settings.
                    </Text>
                  )}
                  <Text style={styles.previewCompanyName}>{businessInfo?.businessName || 'Your Business Name'}</Text>
                  <Text style={styles.previewCompanyDetails}>
                    {(() => {
                      const bInfo = businessInfo || {};
                      const addressParts = [];
                      
                      if (bInfo.firstName || bInfo.lastName) {
                        const fullName = [bInfo.firstName, bInfo.lastName].filter(Boolean).join(' ');
                        addressParts.push(fullName);
                      }
                      
                      if (bInfo.address) addressParts.push(bInfo.address);
                      if (bInfo.city && bInfo.state) {
                        const cityState = bInfo.zip ? `${bInfo.city}, ${bInfo.state} ${bInfo.zip}` : `${bInfo.city}, ${bInfo.state}`;
                        addressParts.push(cityState);
                      } else if (bInfo.city) {
                        addressParts.push(bInfo.city);
                      } else if (bInfo.state) {
                        addressParts.push(bInfo.state);
                      }
                      
                      if (bInfo.phone) {
                        addressParts.push(`Phone: ${bInfo.phone}`);
                      }
                      if (bInfo.email) {
                        addressParts.push(`Email: ${bInfo.email}`);
                      }
                      
                      if (addressParts.length === 0 && !bInfo.businessName) {
                        return '123 Business Street\nCity, State 12345\nPhone: (555) 123-4567\nEmail: info@yourbusiness.com';
                      }
                      
                      return addressParts.join('\n') || 'Contact information not available';
                    })()}
                  </Text>
                </View>
                <View style={styles.previewInvoiceInfo}>
                  <Text style={styles.previewInvoiceTitle}>INVOICE</Text>
                  <Text style={styles.previewInvoiceDetails}>
                    Invoice #: INV-{invoice.id || 'XXXX'}{'\n'}
                    Date: {new Date().toLocaleDateString()}{'\n'}
                    Due Date: {new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}
                  </Text>
                </View>
              </View>

              {/* Customer Section */}
              <View style={styles.previewCustomerSection}>
                <Text style={styles.previewSectionTitle}>Invoice To:</Text>
                <View style={styles.previewCustomerInfo}>
                  <Text style={styles.previewCustomerName}>{invoice.customer || 'Customer Name'}</Text>
                  {(() => {
                    const customer = customers.find(c => c.name === invoice.customer);
                    if (customer) {
                      return (
                        <>
                          {customer.address && <Text style={styles.previewCustomerDetail}>{customer.address}</Text>}
                          {customer.city && customer.state && (
                            <Text style={styles.previewCustomerDetail}>
                              {customer.city}, {customer.state} {customer.zip || ''}
                            </Text>
                          )}
                          {customer.phone && <Text style={styles.previewCustomerDetail}>Phone: {customer.phone}</Text>}
                          {customer.email && <Text style={styles.previewCustomerDetail}>Email: {customer.email}</Text>}
                        </>
                      );
                    }
                    return null;
                  })()}
                </View>
              </View>

              {/* Services Section */}
              <View style={styles.previewServicesSection}>
                <Text style={styles.previewSectionTitle}>Services Provided</Text>
                <View style={styles.previewServiceItem}>
                  <Text style={styles.previewServiceTitle}>{invoice.title || 'Service Title'}</Text>
                  <Text style={styles.previewServiceDescription}>{invoice.description || 'No details provided.'}</Text>
                  <Text style={styles.previewServiceDate}>
                    Work completed on: {(() => {
                      const utcDate = new Date(invoice.selectedDate + 'T' + invoice.selectedTime);
                      const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
                      const formattedDate = localDate.toLocaleDateString();
                      const formattedTime = localDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                      return `${formattedDate} at ${formattedTime}`;
                    })()}
                  </Text>
                </View>
              </View>

              {/* Pricing Section */}
              <View style={styles.previewPricingSection}>
                <View style={styles.previewPriceRow}>
                  <Text style={styles.previewPriceLabel}>Service Total:</Text>
                  <Text style={styles.previewPriceAmount}>${parseFloat(invoice.price || 0).toFixed(2)}</Text>
                </View>
                <View style={[styles.previewPriceRow, styles.previewPriceRowTotal]}>
                  <Text style={styles.previewTotalLabel}>Amount Due:</Text>
                  <Text style={styles.previewTotalAmount}>${parseFloat(invoice.price || 0).toFixed(2)}</Text>
                </View>
              </View>

              {/* Payment Section */}
              <View style={styles.previewPaymentSection}>
                <Text style={styles.previewPaymentTitle}>Payment Information</Text>
                <Text style={styles.previewPaymentText}>
                  • Payment is due within 30 days of invoice date{'\n'}
                  • Please include invoice number with payment{'\n'}
                  • Late payments may be subject to additional fees{'\n'}
                  • Contact us with any questions about this invoice
                </Text>
              </View>

              {/* Footer */}
              <View style={styles.previewFooter}>
                <Text style={styles.previewFooterText}>Thank you for your business!</Text>
                <Text style={styles.previewFooterText}>
                  <Text style={{ fontWeight: 'bold' }}>Questions?</Text> Contact us{(() => {
                    const bInfo = businessInfo || {};
                    const contactParts = [];
                    
                    if (bInfo.phone) {
                      contactParts.push(`at ${bInfo.phone}`);
                    }
                    
                    if (bInfo.email) {
                      contactParts.push(`${contactParts.length > 0 ? 'or ' : 'at '}${bInfo.email}`);
                    }
                    
                    if (contactParts.length === 0) {
                      contactParts.push('at (555) 123-4567 or info@yourbusiness.com');
                    }
                    
                    return contactParts.length > 0 ? ' ' + contactParts.join(' ') : '';
                  })()}
                </Text>
              </View>
            </ScrollView>
            
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.emailModalButton, sendingEmail && { opacity: 0.7 }]}
                onPress={async () => {
                  setPreviewOpen(false);
                  emailInvoicePDF();
                }}
                disabled={sendingEmail}
              >
                <Text style={styles.previewActionButtonText}>
                  {sendingEmail ? 'Sending…' : '📧 One-Touch Email'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity 
                style={[styles.modalButton, styles.savePdfButton]} 
                onPress={generateAndSharePDF}
              >
                <Text style={styles.previewActionButtonText}>💾 Save/Share PDF</Text>
              </TouchableOpacity>
            </View>

            <TouchableOpacity 
              style={styles.closePreviewLink}
              onPress={() => setPreviewOpen(false)}
            >
              <Text style={styles.closePreviewButtonText}>Close Preview</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Custom Modal */}
      {customModal.open && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>{customModal.title}</Text>
            <Text style={styles.modalMessage}>{customModal.message}</Text>
            <View style={styles.modalButtonGroup}>
              {customModal.buttons.map((button, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.modalActionButton,
                    button.style === 'primary' && { backgroundColor: 'blue' },
                    button.style === 'secondary' && { backgroundColor: 'gray' },
                    button.style === 'danger' && { backgroundColor: 'red' },
                  ]}
                  onPress={button.onPress}
                >
                  <Text style={[styles.modalActionButtonText, { color: 'white' }]}>
                    {button.text}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

const htmlInputStyle = {
  padding: 10,
  border: '1px solid #ccc',
  borderRadius: 5,
  outline: 'none',
  fontSize: 14,
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    backgroundColor: '#f0f8ff',
  },
  label: {
    fontWeight: 'bold',
    marginTop: 15,
    fontSize: 20,
    color: '#333',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 5,
  },
  value: {
    fontSize: 16,
    marginTop: 5,
    color: '#555',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 8,
    borderRadius: 4,
    marginTop: 5,
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30,
  },
  button: {
    flex: 1,
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    marginHorizontal: 20,
  },
  editButton: { backgroundColor: 'blue' },
  deleteButton: {
    backgroundColor: '#FF3B30',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 2,
  },
  emailButton: {
    backgroundColor: '#007AFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 3,
    elevation: 2,
  },
  buttonText: { color: 'white', fontWeight: 'bold', fontSize: 14 },
  buttonContainer: { marginTop: 30 },
  saveButton: {
    backgroundColor: '#34C759',
  },
  saveButtonText: { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  cancelButton: {
    backgroundColor: '#FF3B30',
  },
  dateTimeRow: { flexDirection: 'row', gap: 8, marginTop: 5 },
  datePreview: { marginTop: 6 },
  datePreviewText: { color: '#007AFF', fontWeight: 'bold' },
  sentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 5,
  },
  sentButtonsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    padding: 8,
    borderRadius: 5,
    alignItems: 'center',
  },
  actionButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  resendButton: {
    backgroundColor: '#34C759',
  },
  previewButton: {
    backgroundColor: '#007AFF',
  },
  previewOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: '40px',
  },
  previewCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 10,
    width: '60%',
    maxWidth: '700px',
    height: '95%',
  },
  previewTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  bold: {
    fontWeight: 'bold',
  },
  buttonGroup: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  previewActionButton: {
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
  },
  previewActionButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  },
  pdfButton: {
    backgroundColor: '#007AFF',
  },
  emailFromPreviewButton: {
    backgroundColor: '#34C759',
  },
  closeButton: {
    backgroundColor: '#FF3B30',
  },
  sentStatusContainer: {
    flexDirection: 'column',
  },
  dateSentText: {
    fontSize: 14,
    color: '#555',
  },
  modalActionButton: {
    padding: 10,
    borderRadius: 5,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 5,
  },
  modalActionButtonText: {
    fontWeight: 'bold',
    fontSize: 14,
  },
  paymentStatusContainer: {
    flexDirection: 'column',
    marginTop: 5,
  },
  paymentContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 30,
    marginTop: 10,
  },
  paymentOption: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 3,
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  checkmark: {
    color: 'white',
    fontWeight: 'bold',
  },
  paymentOptionText: {
    fontSize: 16,
    color: '#555',
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCard: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 10,
    width: '80%',
    maxWidth: '700px',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  modalMessage: {
    fontSize: 16,
    marginBottom: 20,
  },
  modalButtonGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  customerDetailsLink: {
    color: '#007AFF',
    fontWeight: 'bold',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 15,
    paddingBottom: 10,
    gap: 10,
  },
  modalButton: {
    flex: 1,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  emailModalButton: {
    backgroundColor: 'green',
  },
  savePdfButton: {
    backgroundColor: 'blue',
  },
  closePreviewLink: {
    alignItems: 'center',
    paddingVertical: 15,
    paddingBottom: 20,
  },
  closePreviewButtonText: {
    color: '#FF3B30',
    fontWeight: 'bold',
    fontSize: 16,
    textDecorationLine: 'underline',
  },
  previewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#dc2626',
  },
  previewCompanyInfo: {
    flex: 1,
    paddingRight: 20,
  },
  previewLogoPlaceholder: {
    fontSize: 12,
    color: '#888',
    fontStyle: 'italic',
    marginBottom: 15,
  },
  previewCompanyName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#dc2626',
    marginBottom: 8,
  },
  previewCompanyDetails: {
    color: '#666',
    fontSize: 13,
    lineHeight: 18,
  },
  previewInvoiceInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  previewInvoiceTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#dc2626',
    marginBottom: 8,
  },
  previewInvoiceDetails: {
    fontSize: 13,
    color: '#666',
    textAlign: 'right',
    lineHeight: 20,
  },
  previewCustomerSection: {
    marginBottom: 20,
  },
  previewSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 15,
    paddingBottom: 5,
    borderBottomWidth: 2,
    borderBottomColor: '#e5e7eb',
  },
  previewCustomerInfo: {
    backgroundColor: '#fef2f2',
    padding: 20,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#dc2626',
  },
  previewCustomerName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 10,
  },
  previewCustomerDetail: {
    color: '#4b5563',
  },
  previewServicesSection: {
    marginBottom: 30,
  },
  previewServiceItem: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 25,
    marginBottom: 20,
  },
  previewServiceTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 15,
    textDecorationLine: 'underline',
  },
  previewServiceDescription: {
    color: '#4b5563',
    marginBottom: 20,
  },
  previewServiceDate: {
    color: '#6b7280',
    fontSize: 14,
    fontStyle: 'italic',
  },
  previewPricingSection: {
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    padding: 25,
    marginBottom: 30,
    borderWidth: 2,
    borderColor: '#dc2626',
  },
  previewPriceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#fecaca',
  },
  previewPriceRowTotal: {
    borderBottomWidth: 0,
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc2626',
    marginTop: 10,
    paddingTop: 20,
    borderTopWidth: 2,
    borderTopColor: '#dc2626',
  },
  previewPriceLabel: {
    fontSize: 18,
    color: '#374151',
  },
  previewPriceAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  previewTotalLabel: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  previewTotalAmount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#dc2626',
  },
  previewPaymentSection: {
    marginTop: 40,
    padding: 20,
    backgroundColor: '#f0fdf4',
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#22c55e',
  },
  previewPaymentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#166534',
    marginBottom: 10,
  },
  previewPaymentText: {
    fontSize: 14,
    color: '#15803d',
  },
  previewFooter: {
    marginTop: 50,
    paddingTop: 20,
    borderTopWidth: 2,
    borderTopColor: '#e5e7eb',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 14,
  },
  previewFooterText: {
    marginBottom: 10,
  },
});
